import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
} from '@mui/material';
import default_theme from 'assets/themes/themes';
import { registerRequest } from '../../redux/auth/authSlice';
import authService from '../../services/authService';
import { blueTextFieldStyles } from 'assets/themes/textFieldStyles';

const RegisterPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, user, isAuthenticated } = useSelector((state) => state.auth);

  const [accountType, setAccountType] = useState('individual');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    country: '',
    city: '',
    phone_number: '',
    company_name: '',
    company_size: '',
    account_type: 'individual',
  });

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [pendingMessage, setPendingMessage] = useState('');
  const [agreedToLegal, setAgreedToLegal] = useState(false);
  const [consentError, setConsentError] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/profile');
    } else if (user) {
      // Check if it's a pending registration response
      if (user.status === 'pending') {
        setPendingMessage('Registration request submitted successfully. Your account will be created after approval by the administrator.');
        setShowSuccessMessage(true);
        const timer = setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'Your registration request has been submitted. You will be notified once your account is approved.'
            }
          });
        }, 5000);
        return () => clearTimeout(timer);
      } else {
        // Individual account - immediate creation
        setShowSuccessMessage(true);
        const timer = setTimeout(() => {
          navigate('/login', {
            state: {
              message: "Thank you for registering. Our team will review your registration first. Once it is verified, you will receive an email confirming that your account is ready and you can log in." //'Registration successful! Please check your email to verify your account before logging in. If you don\'t see the email, please check your spam folder.'
            }
          });
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [user, isAuthenticated, navigate]);

  const handleAccountTypeChange = (e) => {
    const newType = e.target.value;
    setAccountType(newType);
    setFormData((prev) => ({
      ...prev,
      account_type: newType,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!agreedToLegal) {
      setConsentError('Please accept the Terms of Use and acknowledge the Privacy Notice to continue.');
      return;
    }

    // Prepare registration data based on account type
    const registrationData = {
      email: formData.email,
      password: formData.password,
      first_name: formData.first_name,
      last_name: formData.last_name,
      account_type: accountType,
      country: formData.country || null,
      city: accountType === 'individual' ? formData.city || null : null,
    };

    // Add organization-specific fields
    if (accountType !== 'individual') {
      registrationData.phone_number = formData.phone_number || null;
      registrationData.company_name = formData.company_name;
      registrationData.company_size = formData.company_size || null;
      // Set organization_type based on account_type
      registrationData.organization_type = accountType.charAt(0).toUpperCase() + accountType.slice(1);
    }

    dispatch(registerRequest(registrationData));
  };

  const isOrganizationAccount = accountType !== 'individual';

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Register
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Create a new account
        </Typography>

        {showSuccessMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {pendingMessage ? 'Registration Request Submitted!' : 'Registration received!'}
            </Typography>
            <Typography variant="body2">
              {pendingMessage || 'Thank you for registering. Our team will review your registration first. Once it is verified, you will receive an email confirming that your account is ready and you can log in.'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              Redirecting to login page...
            </Typography>
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          {/* Account Type Selection */}
          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend" sx={{ mb: 1 }} align="center">
              Account Type
            </FormLabel>
            <RadioGroup
              row
              value={accountType}
              onChange={handleAccountTypeChange}
              sx={{ justifyContent: 'center' }}
            >
              <FormControlLabel value="individual" control={<Radio
                sx={{
                  color: default_theme.palette.other.figmaBlue,
                  transition: '0.2s',
                  '&:hover': {
                    color: 'primary.light',
                  },
                  '&.Mui-checked': {
                    color: default_theme.palette.other.figmaBlue,
                  },
                }}
              />} label="Individual" />
              
              {/* <FormControlLabel value="university" control={<Radio />} label="University" />
              <FormControlLabel value="municipality" control={<Radio />} label="Municipality" />
              <FormControlLabel value="geopark" control={<Radio />} label="Geopark" /> */}
            </RadioGroup>
          </FormControl>

          <Grid container spacing={2}>
            {/* Individual Account Form */}
            {!isOrganizationAccount && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    sx={blueTextFieldStyles(default_theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    sx={blueTextFieldStyles(default_theme)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    sx={blueTextFieldStyles(default_theme)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    inputProps={{ minLength: 8 }}
                    sx={blueTextFieldStyles(default_theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    sx={blueTextFieldStyles(default_theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    sx={blueTextFieldStyles(default_theme)}
                  />
                </Grid>
              </>
            )}

            {/* Organization Account Form */}
            {isOrganizationAccount && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    sx={blueTextFieldStyles(default_theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    sx={blueTextFieldStyles(default_theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Work Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    sx={blueTextFieldStyles(default_theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    required
                    sx={blueTextFieldStyles(default_theme)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Organization Type"
                    value={accountType.charAt(0).toUpperCase() + accountType.slice(1)}
                    disabled
                    helperText="Organization type is determined by your account type selection"
                    sx={blueTextFieldStyles(default_theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    sx={blueTextFieldStyles(default_theme)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Company Size"
                    name="company_size"
                    value={formData.company_size}
                    onChange={handleChange}
                    required
                    sx={blueTextFieldStyles(default_theme)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    sx={blueTextFieldStyles(default_theme)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    inputProps={{ minLength: 8 }}
                    sx={blueTextFieldStyles(default_theme)}
                  />
                </Grid>
              </>
            )}
          </Grid>

          {/* Legal consent (required for all account types) */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <FormControlLabel
              sx={{ alignItems: 'flex-start' }}
              control={
                <Checkbox
                  checked={agreedToLegal}
                  onChange={(e) => {
                    setAgreedToLegal(e.target.checked);
                    if (e.target.checked) setConsentError('');
                  }}
                  sx={{
                    color: default_theme.palette.other.figmaBlue,
                    '&.Mui-checked': {
                      color: default_theme.palette.other.figmaBlue,
                    },
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    // Align the first line with the checkbox box (9px checkbox
                    // padding + half of the 24px icon = ~21px centre).
                    pt: '11px',
                  }}
                >
                  I agree to the{' '}
                  <Link
                    to="/terms"
                    style={{ color: default_theme.palette.other.linkBlue, textDecoration: 'none' }}
                  >
                    Terms of Use
                  </Link>
                  {' '}and acknowledge the{' '}
                  <Link
                    to="/privacy"
                    style={{ color: default_theme.palette.other.linkBlue, textDecoration: 'none' }}
                  >
                    Privacy Notice
                  </Link>
                  .
                </Typography>
              }
            />
            {consentError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {consentError}
              </Alert>
            )}
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
          <Box textAlign="center" sx={{ mt: 2 }}>
            <Link to="/login" style={{ textDecoration: 'none' }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'text.primary'
                  }
                }}
              >
                Already have an account? Login here
              </Typography>
            </Link>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default RegisterPage;
